import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2, ShieldCheck, Check, X } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { LEGAL_VERSIONS } from "@/lib/legalVersions";

const STORAGE_KEY = "useen_cookie_consent";
const CONSENT_VERSION = "v1";
export const OPEN_PREFS_EVENT = "useen:open-cookie-prefs";

// Backend ledger — Batch 2B. Banner still writes localStorage even if this
// fails; KVKK ispat yükü için server-side kayıt tutmak istiyoruz.
const API = process.env.REACT_APP_BACKEND_URL || "";

function recordCookieConsentServerSide({ choice, lang }) {
  try {
    fetch(`${API}/api/consent/cookies`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept-Language": lang || "tr" },
      // Best-effort; do NOT block UI. Reject silently — banner still closes.
      body: JSON.stringify({ choice, version: LEGAL_VERSIONS.cookies, lang }),
      // Keepalive lets the request finish even if the user navigates away.
      keepalive: true,
    }).catch(() => { /* swallow — see comment above */ });
  } catch {
    // Same — never let banner UX break.
  }
}

const COPY = {
  tr: {
    bannerAria: "Çerez tercihleriniz",
    bannerTitle: "Çerez Tercihleriniz",
    bannerBodyDesktop:
      "uSeen, siteyi güvenli ve düzgün çalıştırmak için zorunlu çerezleri kullanır. Analitik ve pazarlama çerezleri ise yalnızca izninizle çalışır.",
    bannerBodyMobile:
      "Zorunlu çerezler her zaman aktiftir. Analitik ve pazarlama çerezleri yalnızca izninizle çalışır.",
    policyLink: "Çerez Politikası",
    acceptAll: "Tümünü Kabul Et",
    onlyNecessaryLong: "Sadece Zorunlu Çerezler",
    onlyNecessaryShort: "Sadece Zorunlu",
    manage: "Tercihleri Yönet",
    modalTitle: "Çerez Tercihleri",
    modalClose: "Kapat",
    modalIntro:
      "Hangi çerez kategorilerine izin vermek istediğinizi seçin. Tercihinizi istediğiniz an Çerez Tercihleri bağlantısından güncelleyebilirsiniz.",
    necessaryTitle: "Zorunlu Çerezler",
    necessaryDesc: "Oturum, güvenlik ve temel platform işlevleri için gereklidir. Kapatılamaz.",
    analyticsTitle: "Analitik Çerezler",
    analyticsDesc: "Hangi sayfaların ne kadar süre kullanıldığını anonim olarak ölçer.",
    marketingTitle: "Pazarlama Çerezleri",
    marketingDesc: "İlgi alanlarınıza uygun ürün iletişimi içindir. Varsayılan olarak kapalıdır.",
    modalOnlyNecessary: "Sadece Zorunlu",
    savePrefs: "Tercihlerimi Kaydet",
    lastSaved: "Son kaydedilen tercih",
    locale: "tr-TR",
    policyHref: "/cerez-politikasi",
  },
  en: {
    bannerAria: "Your cookie preferences",
    bannerTitle: "Your Cookie Preferences",
    bannerBodyDesktop:
      "uSeen uses necessary cookies to run the site securely and properly. Analytics and marketing cookies only run with your consent.",
    bannerBodyMobile:
      "Necessary cookies are always on. Analytics and marketing cookies only run with your consent.",
    policyLink: "Cookie Policy",
    acceptAll: "Accept All",
    onlyNecessaryLong: "Necessary Cookies Only",
    onlyNecessaryShort: "Necessary Only",
    manage: "Manage Preferences",
    modalTitle: "Cookie Preferences",
    modalClose: "Close",
    modalIntro:
      "Choose which cookie categories you allow. You can update your preference any time from the Cookie Preferences link.",
    necessaryTitle: "Necessary Cookies",
    necessaryDesc: "Required for session, security and core platform features. Cannot be turned off.",
    analyticsTitle: "Analytics Cookies",
    analyticsDesc: "Measure anonymously which pages are used and for how long.",
    marketingTitle: "Marketing Cookies",
    marketingDesc: "Used for product communication relevant to your interests. Off by default.",
    modalOnlyNecessary: "Necessary Only",
    savePrefs: "Save Preferences",
    lastSaved: "Last saved preference",
    locale: "en-US",
    policyHref: "/en/cookie-policy",
  },
};

/**
 * Cookie consent banner + preference modal.
 *  • Banner shows on first visit (no localStorage entry, or stored version != CONSENT_VERSION).
 *  • Three CTAs: Accept all / Necessary only / Manage preferences.
 *  • Modal lets user toggle analytics + marketing categories; necessary is forced on.
 *  • Consent persists to localStorage under `useen_cookie_consent`.
 *  • PostHog (the only analytics script currently in /public/index.html) is gated via
 *    `posthog.opt_in_capturing()` / `posthog.opt_out_capturing()` whenever consent changes.
 *  • Other code can dispatch `useen:open-cookie-prefs` (CustomEvent) to re-open the modal —
 *    used by the footer "Çerez Tercihleri" link.
 */
export default function CookieConsent() {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.tr;
  const [consent, setConsent] = useState(null); // null while reading localStorage
  const [bannerOpen, setBannerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Hydrate from localStorage on mount + apply analytics opt-in/out
  useEffect(() => {
    let stored = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.consent_version === CONSENT_VERSION) {
          stored = parsed;
        }
      }
    } catch {
      stored = null;
    }
    if (stored) {
      setConsent(stored);
      setAnalytics(!!stored.analytics);
      setMarketing(!!stored.marketing);
      applyAnalytics(stored.analytics);
    } else {
      // Default: opt out of analytics until the user explicitly chooses.
      applyAnalytics(false);
      setBannerOpen(true);
    }
  }, []);

  // Footer / global listener to reopen the modal
  useEffect(() => {
    const handler = () => setModalOpen(true);
    window.addEventListener(OPEN_PREFS_EVENT, handler);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, handler);
  }, []);

  const save = (next) => {
    const payload = {
      necessary: true,
      analytics: !!next.analytics,
      marketing: !!next.marketing,
      consent_at: new Date().toISOString(),
      consent_version: CONSENT_VERSION,
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    setConsent(payload);
    setAnalytics(payload.analytics);
    setMarketing(payload.marketing);
    setBannerOpen(false);
    setModalOpen(false);
    applyAnalytics(payload.analytics);
    // Batch 2B — fire-and-forget server-side ledger. Banner UX is unaffected
    // by failure. Choice is "accepted" if ANY non-necessary category is on,
    // else "rejected". This matches the user-facing Accept / Necessary Only
    // semantics.
    const choice = (payload.analytics || payload.marketing) ? "accepted" : "rejected";
    recordCookieConsentServerSide({ choice, lang });
  };

  const acceptAll = () => save({ analytics: true, marketing: true });
  const onlyNecessary = () => save({ analytics: false, marketing: false });
  const savePreferences = () => save({ analytics, marketing });

  return (
    <>
      {bannerOpen && !modalOpen && (
        <div
          className="fixed bottom-3 right-3 left-3 sm:bottom-4 sm:right-4 sm:left-auto sm:w-[440px] z-[100] rounded-2xl glass-strong border border-cyan-500/20 p-3.5 sm:p-5 shadow-[0_20px_60px_-15px_rgba(34,211,238,0.25)]"
          role="dialog"
          aria-label={t.bannerAria}
          data-testid="cookie-banner"
        >
          <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
              <Cookie size={12} className="text-cyan-300 sm:hidden" />
              <Cookie size={14} className="text-cyan-300 hidden sm:block" />
            </div>
            <div className="font-display font-semibold text-white text-sm sm:text-base">{t.bannerTitle}</div>
          </div>
          <p className="text-[11px] sm:text-xs text-slate-300 leading-snug sm:leading-relaxed mb-3 sm:mb-4">
            <span className="hidden sm:inline">
              {t.bannerBodyDesktop}{" "}
            </span>
            <span className="sm:hidden">
              {t.bannerBodyMobile}{" "}
            </span>
            <Link to={t.policyHref} className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2" data-testid="cookie-banner-policy-link">
              {t.policyLink}
            </Link>
          </p>
          <div className="flex flex-row gap-2">
            <button
              onClick={acceptAll}
              className="neon-btn rounded-lg px-3 py-1.5 sm:px-3.5 sm:py-2 text-[11px] sm:text-xs font-medium text-white flex-1"
              data-testid="cookie-accept-all"
            >
              {t.acceptAll}
            </button>
            <button
              onClick={onlyNecessary}
              className="ghost-btn rounded-lg px-3 py-1.5 sm:px-3.5 sm:py-2 text-[11px] sm:text-xs text-white flex-1 border border-white/10"
              data-testid="cookie-only-necessary"
            >
              <span className="hidden sm:inline">{t.onlyNecessaryLong}</span>
              <span className="sm:hidden">{t.onlyNecessaryShort}</span>
            </button>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="mt-2 sm:mt-3 w-full text-[10px] sm:text-[11px] text-slate-400 hover:text-cyan-300 hidden sm:inline-flex items-center justify-center gap-1.5"
            data-testid="cookie-manage"
          >
            <Settings2 size={11} /> {t.manage}
          </button>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-4" data-testid="cookie-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl glass-strong border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Cookie size={16} className="text-cyan-300" />
                <div className="font-display font-semibold text-white">{t.modalTitle}</div>
              </div>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-white" data-testid="cookie-modal-close" aria-label={t.modalClose}>
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-slate-400 mb-4 leading-relaxed">
              {t.modalIntro}
            </p>

            <div className="space-y-2.5">
              <CategoryRow
                title={t.necessaryTitle}
                desc={t.necessaryDesc}
                icon={<ShieldCheck size={14} className="text-emerald-300" />}
                checked
                disabled
                testid="cookie-cat-necessary"
              />
              <CategoryRow
                title={t.analyticsTitle}
                desc={t.analyticsDesc}
                checked={analytics}
                onChange={setAnalytics}
                testid="cookie-cat-analytics"
              />
              <CategoryRow
                title={t.marketingTitle}
                desc={t.marketingDesc}
                checked={marketing}
                onChange={setMarketing}
                testid="cookie-cat-marketing"
              />
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <button
                onClick={onlyNecessary}
                className="ghost-btn rounded-lg px-4 py-2.5 text-xs text-white border border-white/10 flex-1"
                data-testid="cookie-modal-only-necessary"
              >
                {t.modalOnlyNecessary}
              </button>
              <button
                onClick={savePreferences}
                className="neon-btn rounded-lg px-4 py-2.5 text-xs font-medium text-white flex-1 inline-flex items-center justify-center gap-1.5"
                data-testid="cookie-save-preferences"
              >
                <Check size={12} /> {t.savePrefs}
              </button>
            </div>

            <div className="mt-4 text-center">
              <Link to={t.policyHref} className="text-[11px] text-slate-400 hover:text-cyan-300" data-testid="cookie-modal-policy-link">
                {t.policyLink}
              </Link>
            </div>

            {consent?.consent_at && (
              <div className="mt-3 text-center text-[10px] text-slate-500">
                {t.lastSaved}: {new Date(consent.consent_at).toLocaleString(t.locale)}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function CategoryRow({ title, desc, checked, onChange, disabled, icon, testid }) {
  return (
    <div className="rounded-xl bg-black/30 border border-white/5 p-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <div className="text-sm text-white font-medium">{title}</div>
        </div>
        <div className="text-[11px] text-slate-400 leading-snug">{desc}</div>
      </div>
      <label className="inline-flex items-center cursor-pointer mt-0.5">
        <input
          type="checkbox"
          checked={!!checked}
          disabled={!!disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
          className="sr-only"
          data-testid={testid}
        />
        <span
          className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
            disabled ? "bg-emerald-500/40" : checked ? "bg-cyan-500" : "bg-white/10"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
              checked ? "translate-x-4" : ""
            }`}
          />
        </span>
      </label>
    </div>
  );
}

function applyAnalytics(allow) {
  // Gate the PostHog snippet loaded in /public/index.html.
  // posthog.opt_in_capturing() / opt_out_capturing() also persist in their own
  // localStorage key, so this stays in sync across reloads even if our key disappears.
  try {
    const ph = typeof window !== "undefined" ? window.posthog : null;
    if (!ph || typeof ph.opt_in_capturing !== "function") return;
    if (allow) ph.opt_in_capturing();
    else ph.opt_out_capturing();
  } catch { /* ignore */ }
}

// Convenience helper for code that wants to ask "did the user allow X?"
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.consent_version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
}
